import React from 'react';
import { Grid } from '@mui/material';
import { useBoardNodesContext } from 'hooks/common';
import { useIsMutating } from 'react-query';

type BoardNodeSelectionWrapperProps = {
  children: React.ReactNode;
  className?: string;
  nodeID: string;
  isEmpty: boolean;
};

// this component also handles empty node borders
// because borders are not applied to by the before pseudo element
// the empty content is handled by getBoardNodeContent
function BoardNodeSelectionWrapper({
  children,
  className,
  nodeID,
  isEmpty,
}: BoardNodeSelectionWrapperProps) {
  const {
    handleAddToNodeSelection,
    selectedNodes,
    setSelectedNodes,
    handleRemoveNodesFromSelection,
  } = useBoardNodesContext();
  const mutation = !!useIsMutating({
    mutationKey: ['addChatMessage'],
  });

  const isNodeSelected = selectedNodes.includes(nodeID);
  const handleAddNodeSelection = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!e.shiftKey) {
      setSelectedNodes([nodeID]);
    }

    if (isNodeSelected) {
      handleRemoveNodesFromSelection(nodeID);
    } else {
      handleAddToNodeSelection(nodeID);
    }
  };

  return (
    <Grid
      container
      component='span'
      onClick={handleAddNodeSelection}
      className={className}
      sx={{
        overflow: 'visible',
        position: 'relative',
        borderRadius: '10px',
        '@keyframes gradient': {
          '0%': {
            backgroundPosition: '0% 50%',
          },
          '50%': {
            backgroundPosition: '100% 50%',
          },
          '100%': {
            backgroundPosition: '0% 50%',
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-2px',
          padding: '2px',
          background: (theme) =>
            isNodeSelected && mutation
              ? 'linear-gradient(95deg, #F48120, #E45282, #DD529C, #6268AF, #F48120)'
              : isNodeSelected
                ? theme.palette.primary.main
                : isEmpty
                  ? theme.palette.warning.main
                  : theme.palette.borderColor,
          backgroundSize: '300% 300%',
          animation:
            isNodeSelected && mutation ? 'gradient 3s ease infinite' : 'none',
          borderRadius: '12px',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        },
        background: '#fff',
      }}
    >
      {children}
    </Grid>
  );
}

export default BoardNodeSelectionWrapper;
